// src/services/chat/chatActionsService.js

import { auth, db } from '../../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  setDoc,
  deleteDoc,
  writeBatch,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  deleteField,
  query,
  limit,
} from 'firebase/firestore';

class ChatActionsService {
  // ── Singleton ──────────────────────────────────────────────────────────────

  constructor() {
    this._db = db;
    this._auth = auth;
    this._archivedIds = new Set();
    this._archiveCacheSeeded = false;
  }

  static getInstance() {
    if (!ChatActionsService._instance) {
      ChatActionsService._instance = new ChatActionsService();
    }
    return ChatActionsService._instance;
  }

  get _uid() {
    return this._auth.currentUser?.uid || '';
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  _chatRef(conversationId) {
    return doc(this._db, 'chats', conversationId);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // ARCHIVE CACHE — synchronous helpers for the UI
  // ─────────────────────────────────────────────────────────────────────────

  isArchivedLocally(conversationId) {
    return this._archivedIds.has(conversationId);
  }

  seedArchiveCache(archivedConversationIds) {
    if (this._archiveCacheSeeded) return;
    for (const id of archivedConversationIds) {
      this._archivedIds.add(id);
    }
    this._archiveCacheSeeded = true;
  }

  clearArchiveCache() {
    this._archivedIds.clear();
    this._archiveCacheSeeded = false;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // PIN / UNPIN
  // ─────────────────────────────────────────────────────────────────────────

  async pinConversation(conversationId) {
    try {
      await setDoc(
        this._chatRef(conversationId),
        {
          pinnedBy: arrayUnion([this._uid]),
        },
        { merge: true }
      );
      console.log('Pinned:', conversationId);
    } catch (error) {
      console.error('pinConversation error:', error);
      throw error;
    }
  }

  async unpinConversation(conversationId) {
    try {
      await updateDoc(this._chatRef(conversationId), {
        pinnedBy: arrayRemove([this._uid]),
      });
      console.log('Unpinned:', conversationId);
    } catch (error) {
      console.error('unpinConversation error:', error);
      throw error;
    }
  }

  async togglePin(conversationId, currentlyPinned) {
    if (currentlyPinned) {
      await this.unpinConversation(conversationId);
      return false;
    } else {
      await this.pinConversation(conversationId);
      return true;
    }
  }

  async isPinned(conversationId) {
    try {
      const snap = await getDoc(this._chatRef(conversationId));
      if (!snap.exists()) return false;
      const data = snap.data();
      const pinnedBy = data.pinnedBy || [];
      return pinnedBy.includes(this._uid);
    } catch (error) {
      console.error('isPinned error:', error);
      return false;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // BLOCK / UNBLOCK
  // ─────────────────────────────────────────────────────────────────────────

  async blockUser(otherUserId) {
    if (!otherUserId || otherUserId === this._uid) return;

    const batch = writeBatch(this._db);

    const blockDocRef = doc(
      this._db,
      'blockedUsers',
      this._uid,
      'blocked',
      otherUserId
    );

    batch.set(blockDocRef, {
      blockedAt: serverTimestamp(),
      blockerId: this._uid,
      blockedId: otherUserId,
    });

    batch.set(
      doc(this._db, 'users', this._uid),
      {
        blockedUsers: arrayUnion([otherUserId]),
      },
      { merge: true }
    );

    try {
      await batch.commit();
      console.log('Blocked:', otherUserId);
    } catch (error) {
      console.error('blockUser error:', error);
      throw error;
    }
  }

  async unblockUser(otherUserId) {
    if (!otherUserId) return;

    const batch = writeBatch(this._db);

    const blockDocRef = doc(
      this._db,
      'blockedUsers',
      this._uid,
      'blocked',
      otherUserId
    );

    batch.delete(blockDocRef);

    batch.update(doc(this._db, 'users', this._uid), {
      blockedUsers: arrayRemove([otherUserId]),
    });

    try {
      await batch.commit();
      console.log('Unblocked:', otherUserId);
    } catch (error) {
      console.error('unblockUser error:', error);
      throw error;
    }
  }

  async isBlocked(otherUserId) {
    if (!otherUserId) return false;
    try {
      const docRef = doc(
        this._db,
        'blockedUsers',
        this._uid,
        'blocked',
        otherUserId
      );
      const snap = await getDoc(docRef);
      return snap.exists();
    } catch (error) {
      console.error('isBlocked error:', error);
      return false;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // MUTE / UNMUTE
  // ─────────────────────────────────────────────────────────────────────────

  async muteConversation(conversationId, duration = null) {
    const muteUntilMs = duration
      ? Date.now() + duration
      : 0;

    try {
      await setDoc(
        this._chatRef(conversationId),
        {
          [`mutedBy.${this._uid}`]: muteUntilMs,
        },
        { merge: true }
      );
      console.log('Muted:', conversationId, 'until', muteUntilMs);
    } catch (error) {
      console.error('muteConversation error:', error);
      throw error;
    }
  }

  async unmuteConversation(conversationId) {
    try {
      const updateData = {};
      updateData[`mutedBy.${this._uid}`] = deleteField();
      await updateDoc(this._chatRef(conversationId), updateData);
      console.log('Unmuted:', conversationId);
    } catch (error) {
      console.error('unmuteConversation error:', error);
      throw error;
    }
  }

  async isMuted(conversationId) {
    try {
      const snap = await getDoc(this._chatRef(conversationId));
      if (!snap.exists()) return false;
      const data = snap.data();
      const mutedBy = data.mutedBy || {};
      
      if (!mutedBy[this._uid]) return false;

      const muteUntilMs = mutedBy[this._uid];
      if (muteUntilMs === 0) return true;

      const now = Date.now();
      if (now >= muteUntilMs) {
        const updateData = {};
        updateData[`mutedBy.${this._uid}`] = deleteField();
        updateDoc(this._chatRef(conversationId), updateData).catch(() => {});
        return false;
      }
      return true;
    } catch (error) {
      console.error('isMuted error:', error);
      return false;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // ARCHIVE / UNARCHIVE
  // ─────────────────────────────────────────────────────────────────────────

  async archiveConversation(conversationId) {
    try {
      await setDoc(
        this._chatRef(conversationId),
        {
          archivedBy: arrayUnion([this._uid]),
        },
        { merge: true }
      );
      this._archivedIds.add(conversationId);
      console.log('Archived:', conversationId);
    } catch (error) {
      console.error('archiveConversation error:', error);
      throw error;
    }
  }

  async unarchiveConversation(conversationId) {
    try {
      await updateDoc(this._chatRef(conversationId), {
        archivedBy: arrayRemove([this._uid]),
      });
      this._archivedIds.delete(conversationId);
      console.log('Unarchived:', conversationId);
    } catch (error) {
      console.error('unarchiveConversation error:', error);
      throw error;
    }
  }

  async isArchived(conversationId) {
    try {
      const snap = await getDoc(this._chatRef(conversationId));
      if (!snap.exists()) return false;
      const data = snap.data();
      const result = (data.archivedBy || []).includes(this._uid);
      
      if (result) {
        this._archivedIds.add(conversationId);
      } else {
        this._archivedIds.delete(conversationId);
      }
      return result;
    } catch (error) {
      console.error('isArchived error:', error);
      return false;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // DELETE
  // ─────────────────────────────────────────────────────────────────────────

  async deleteConversation(conversationId, participants = []) {
    if (!this._uid) return;

    try {
      const chatRef = this._chatRef(conversationId);
      const snap = await getDoc(chatRef);
      if (!snap.exists()) return;

      const data = snap.data();
      const allParticipants = data.allParticipants || data.participants || participants;

      await setDoc(
        chatRef,
        {
          allParticipants: allParticipants,
          deletedBy: arrayUnion([this._uid]),
          participants: arrayRemove([this._uid]),
        },
        { merge: true }
      );

      const updatedSnap = await getDoc(chatRef);
      if (!updatedSnap.exists()) return;

      const updatedData = updatedSnap.data();
      const deletedBy = updatedData.deletedBy || [];

      const allDeleted = allParticipants.every(p => deletedBy.includes(p));

      if (allDeleted) {
        await this._hardDeleteConversation(conversationId, chatRef);
        console.log('Hard-deleted conversation:', conversationId);
      } else {
        console.log('Soft-deleted conversation:', conversationId);
      }
    } catch (error) {
      console.error('deleteConversation error:', error);
      throw error;
    }
  }

  async _hardDeleteConversation(conversationId, chatRef) {
    const batchSize = 500;
    while (true) {
      const messagesRef = collection(chatRef, 'messages');
      const q = query(messagesRef, limit(batchSize));
      const snap = await getDocs(q);
      
      if (snap.empty) break;

      const batch = writeBatch(this._db);
      for (const docSnap of snap.docs) {
        batch.delete(docSnap.ref);
      }
      await batch.commit();
    }
    await deleteDoc(chatRef);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // MARK AS READ
  // ─────────────────────────────────────────────────────────────────────────

  async markAsRead(conversationId) {
    try {
      await setDoc(
        this._chatRef(conversationId),
        {
          [`unreadCount.${this._uid}`]: 0,
          [`lastSeen.${this._uid}`]: serverTimestamp(),
        },
        { merge: true }
      );
      console.log('Marked as read:', conversationId);
    } catch (error) {
      console.error('markAsRead error:', error);
      throw error;
    }
  }

  async markMultipleAsRead(conversationIds) {
    await Promise.all(conversationIds.map(id => this.markAsRead(id)));
  }

  // ─────────────────────────────────────────────────────────────────────────
  // BATCH HELPERS
  // ─────────────────────────────────────────────────────────────────────────

  async pinMultiple(ids) {
    await Promise.all(ids.map(id => this.pinConversation(id)));
  }

  async unpinMultiple(ids) {
    await Promise.all(ids.map(id => this.unpinConversation(id)));
  }

  async muteMultiple(ids, duration = null) {
    await Promise.all(ids.map(id => this.muteConversation(id, duration)));
  }

  async unmuteMultiple(ids) {
    await Promise.all(ids.map(id => this.unmuteConversation(id)));
  }

  async archiveMultiple(ids) {
    await Promise.all(ids.map(id => this.archiveConversation(id)));
  }

  async unarchiveMultiple(ids) {
    await Promise.all(ids.map(id => this.unarchiveConversation(id)));
  }

  async deleteMultiple(ids, participantsMap = {}) {
    await Promise.all(
      ids.map(id => this.deleteConversation(id, participantsMap[id] || []))
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // DISPOSE
  // ─────────────────────────────────────────────────────────────────────────

  dispose() {
    this._archivedIds.clear();
    this._archiveCacheSeeded = false;
  }
}

// ── Singleton export ─────────────────────────────────────────────────────────

const chatActionsService = ChatActionsService.getInstance();
export { chatActionsService };
export default chatActionsService;